import { Badge, EmptyState } from '@/components/ui';
import { formatDate } from '@/utils/format';

import { formatMoney, invoiceLabel, invoiceTone } from './money';

/**
 * Billing history.
 *
 * An overdue invoice is drawn differently from a merely open one, because the
 * two need different things from the reader — one is a note, the other is a
 * task.
 */
/**
 * `emptyTitle` / `emptyDescription` let an admin screen say what an empty
 * list means from where the admin is standing; the defaults are written for a
 * company looking at its own bill.
 */
export function InvoiceTable({
  invoices,
  action,
  showCustomer = false,
  emptyTitle = 'No invoices yet',
  emptyDescription = 'The free plan is not billed, so there is nothing here until you move to a paid one.',
}) {
  if (invoices.length === 0) {
    return <EmptyState compact title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-ink-muted border-line border-b text-left">
            <th className="py-2 pr-4 font-medium">Invoice</th>
            {showCustomer && <th className="py-2 pr-4 font-medium">Customer</th>}
            <th className="py-2 pr-4 font-medium">For</th>
            <th className="py-2 pr-4 font-medium">Issued</th>
            <th className="py-2 pr-4 font-medium">Due</th>
            <th className="py-2 pr-4 text-right font-medium">Amount</th>
            <th className="py-2 pr-4 font-medium">Status</th>
            {action && <th className="py-2 font-medium" />}
          </tr>
        </thead>
        <tbody className="divide-line divide-y">
          {invoices.map((invoice) => (
            <tr key={invoice.id}>
              <td className="text-ink py-3 pr-4 font-medium whitespace-nowrap">{invoice.number}</td>
              {showCustomer && (
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-2">
                    <span className="text-ink font-medium">{invoice.customer_name ?? '—'}</span>
                    {invoice.customer_type && (
                      <Badge tone={invoice.customer_type === 'student' ? 'brand' : 'neutral'}>
                        {invoice.customer_type === 'student' ? 'Student' : 'Company'}
                      </Badge>
                    )}
                  </div>
                  {invoice.customer_email && (
                    <p className="text-ink-muted text-xs">{invoice.customer_email}</p>
                  )}
                </td>
              )}
              <td className="text-ink-muted min-w-48 py-3 pr-4">{invoice.description}</td>
              <td className="text-ink-muted py-3 pr-4 whitespace-nowrap">
                {formatDate(invoice.issued_at)}
              </td>
              <td className="text-ink-muted py-3 pr-4 whitespace-nowrap">
                {formatDate(invoice.due_at)}
              </td>
              <td className="text-ink py-3 pr-4 text-right whitespace-nowrap">
                {formatMoney(invoice.amount_cents, invoice.currency)}
              </td>
              <td className="py-3 pr-4">
                <Badge tone={invoiceTone(invoice)}>{invoiceLabel(invoice)}</Badge>
              </td>
              {action && <td className="py-3">{action(invoice)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
